import { useTranslate, HttpError } from '@refinedev/core';
import { Create, useForm } from '@refinedev/antd';
import { Form, Input, Radio, Select, Switch, Tabs, Divider } from 'antd';
import type {
  EmotionDetailOut,
  EmotionCreateIn,
} from '@/api/generated/schemas';
import { ModStatus, EmotionKind, ImageKind } from '@/api/generated/schemas';
import { CollectionImageFields } from '@/components/common/CollectionImageFields';
import { ImageUploadField } from '@/components/common/ImageUploadField';
import { SearchPopularityField } from '@/components/common/SearchPopularityField';
import {
  CollectionSortingList,
  normalizeCollectionSorting,
} from '@/components/query-builder';

export const EmotionCreate = () => {
  const t = useTranslate();
  const { formProps, saveButtonProps, form } = useForm<
    EmotionDetailOut,
    HttpError,
    EmotionCreateIn
  >({
    resource: 'user_catalog.emotions',
  });

  const isFeatured = Form.useWatch('is_featured', form);

  const handleFinish = (values: EmotionCreateIn) =>
    formProps.onFinish?.({
      ...values,
      publication_custom_sorting: normalizeCollectionSorting(
        values.publication_custom_sorting
      ),
    });

  return (
    <Create saveButtonProps={saveButtonProps}>
      <Form {...formProps} layout="vertical" onFinish={handleFinish}>
        <Tabs
          items={[
            {
              key: 'basic',
              label: t('base.sections.basic_info'),
              children: (
                <>
                  <Form.Item
                    name="name"
                    label={t('user_catalog.emotions.fields.name')}
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="kind"
                    label={t('user_catalog.emotions.fields.kind')}
                    rules={[{ required: true }]}
                  >
                    <Select
                      options={[
                        {
                          label: t('user_catalog.emotions.kind.publication'),
                          value: EmotionKind.publication,
                        },
                        {
                          label: t('user_catalog.emotions.kind.lector'),
                          value: EmotionKind.lector,
                        },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item
                    name="is_promoted"
                    label={t('user_catalog.emotions.fields.is_promoted')}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item name="icon_storage_key" hidden>
                    <Input />
                  </Form.Item>
                  <ImageUploadField
                    imageKind={ImageKind.emotion_icons}
                    onUpload={(key) =>
                      form?.setFieldsValue({ icon_storage_key: key })
                    }
                    label={t('user_catalog.emotions.fields.icon_url')}
                  />

                  <Form.Item
                    name="is_featured"
                    label={t('user_catalog.emotions.fields.is_featured')}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>

                  <ImageUploadField
                    imageKind={ImageKind.emotion_images}
                    disabled={!isFeatured}
                    onUpload={(key) =>
                      form?.setFields([
                        { name: 'image_storage_key', value: key, errors: [] },
                      ])
                    }
                    label={t('user_catalog.emotions.fields.image_url')}
                  />
                  <Form.Item
                    name="image_storage_key"
                    dependencies={['is_featured']}
                    rules={[
                      {
                        validator: (_, value: string) => {
                          if (form.getFieldValue('is_featured') && !value) {
                            return Promise.reject(
                              new Error(
                                t(
                                  'user_catalog.emotions.validation.image_required_when_featured'
                                )
                              )
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input type="hidden" />
                  </Form.Item>

                  <Divider>{t('base.sections.system_info')}</Divider>

                  <Form.Item
                    name="mod_status"
                    label={t('base.fields.mod_status')}
                    rules={[{ required: true }]}
                    initialValue={ModStatus.enabled}
                  >
                    <Radio.Group
                      optionType="button"
                      buttonStyle="solid"
                      options={Object.values(ModStatus).map((value) => ({
                        label: t(`base.mod_status.${value}`),
                        value,
                      }))}
                    />
                  </Form.Item>
                </>
              ),
            },
            {
              key: 'collection',
              label: t('base.sections.collection_info'),
              children: (
                <>
                  <Form.Item
                    name="header"
                    label={t('user_catalog.emotions.fields.header')}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="content"
                    label={t('user_catalog.emotions.fields.content')}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>

                  <CollectionImageFields />

                  <Divider>{t('base.collection.sorting')}</Divider>
                  <Form.Item extra={t('base.collection.sorting_hint')}>
                    <CollectionSortingList />
                  </Form.Item>
                </>
              ),
            },
            {
              key: 'search',
              label: t('user_catalog.common.tabs.search'),
              children: <SearchPopularityField />,
            },
          ]}
        />
      </Form>
    </Create>
  );
};
